import { useCallback, useEffect, useState } from 'react';

/**
 * Photo timeline (sub-spec 07 Plan 4 / spec §9).
 *
 * Renders a booking's photo timeline newest-first. Used by:
 *   - Customer `/account/bookings/:id` (scope='customer')
 *   - Staff `/bookings/:id` (scope='staff')
 *   - Public `/stay/:token` (scope='public', requires shareToken prop)
 *
 * Realtime updates: when an external `realtimeNonce` prop bumps (a parent
 * subscribed to AccountRealtimeDO has received a `BOOKING_PHOTO_POSTED`
 * event for this bookingId), the timeline re-fetches the top page.
 *
 * Each photo renders a thumbnail via `/api/files/:fileId/url?variant=thumb`;
 * clicking opens the full-size variant in a lightbox.
 */

export interface PhotoTimelinePhoto {
  id: string;
  bookingId: string;
  fileId: string;
  caption: string | null;
  takenAt: string;
  createdAt: string;
  postedByMembershipId: string | null;
  postedByCustomerUserId: string | null;
}

export type PhotoTimelineScope = 'staff' | 'customer' | 'public';

export interface PhotoTimelineProps {
  bookingId: string;
  scope: PhotoTimelineScope;
  /** Required when scope='public'. */
  shareToken?: string;
  /**
   * Bump to force a re-fetch. Parents subscribed to the realtime DO can
   * increment this when a `BOOKING_PHOTO_POSTED` event arrives for this
   * bookingId.
   */
  realtimeNonce?: number;
}

function listEndpoint(props: PhotoTimelineProps): string {
  if (props.scope === 'public') {
    if (!props.shareToken) throw new Error('PhotoTimeline: shareToken required for scope=public');
    return `/api/public/stay/${encodeURIComponent(props.shareToken)}/photos`;
  }
  if (props.scope === 'customer') {
    return `/api/customer/bookings/${encodeURIComponent(props.bookingId)}/photos`;
  }
  return `/api/bookings/${encodeURIComponent(props.bookingId)}/photos`;
}

function thumbUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/url?variant=thumb`;
}

function fullUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/url?variant=full`;
}

export function PhotoTimeline(props: PhotoTimelineProps) {
  const [photos, setPhotos] = useState<PhotoTimelinePhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lightboxFileId, setLightboxFileId] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (before: string | null, replace: boolean) => {
      try {
        const url = new URL(listEndpoint(props), window.location.origin);
        url.searchParams.set('limit', '24');
        if (before) url.searchParams.set('before', before);
        const res = await fetch(url.pathname + url.search, { credentials: 'include' });
        if (!res.ok) {
          setError(`Could not load photos (${res.status})`);
          return;
        }
        const body = (await res.json()) as { photos: PhotoTimelinePhoto[] };
        const next = body.photos ?? [];
        setError(null);
        setPhotos((prev) => (replace ? next : [...prev, ...next]));
        setHasMore(next.length === 24);
        if (next.length > 0) {
          setCursor(next[next.length - 1]?.takenAt ?? null);
        } else {
          setCursor(null);
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    // listEndpoint depends on these primitive props
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.scope, props.bookingId, props.shareToken],
  );

  // Initial load + re-fetch when realtimeNonce bumps.
  useEffect(() => {
    setLoading(true);
    setPhotos([]);
    setCursor(null);
    setHasMore(true);
    fetchPage(null, true);
  }, [fetchPage, props.realtimeNonce]);

  const loadMore = () => {
    if (!cursor || !hasMore) return;
    fetchPage(cursor, false);
  };

  if (loading && photos.length === 0) {
    return (
      <div data-testid="photo-timeline-loading" style={{ padding: '1rem 0' }}>
        Loading photos…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="photo-timeline-error" role="alert" style={{ padding: '1rem 0' }}>
        {error}
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div data-testid="photo-timeline-empty" style={{ padding: '1rem 0' }}>
        No photos yet.
      </div>
    );
  }

  return (
    <div data-testid="photo-timeline">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '0.75rem',
        }}
      >
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            data-testid={`photo-timeline-item-${photo.id}`}
            onClick={() => setLightboxFileId(photo.fileId)}
            style={{
              padding: 0,
              border: 'none',
              background: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <img
              src={thumbUrl(photo.fileId)}
              alt={photo.caption ?? 'Photo'}
              loading="lazy"
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                borderRadius: '8px',
                background: 'oklch(0.95 0.005 75)',
              }}
            />
            {photo.caption ? (
              <div
                style={{
                  fontSize: '0.78rem',
                  marginTop: '0.35rem',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                }}
              >
                {photo.caption}
              </div>
            ) : null}
          </button>
        ))}
      </div>
      {hasMore ? (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            data-testid="photo-timeline-load-more"
            onClick={loadMore}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid oklch(0.85 0.008 75)',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            Load more
          </button>
        </div>
      ) : null}

      {lightboxFileId ? (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="photo-timeline-lightbox"
          onClick={() => setLightboxFileId(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setLightboxFileId(null);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '2rem',
          }}
        >
          <img
            src={fullUrl(lightboxFileId)}
            alt="Photo"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: '8px',
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
